import React from "react";
import PropTypes from "prop-types";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";
import { returnUpDownClassname } from "utils/helpers";

// All headline numbers of a backtest in one card, grouped by what they answer.
// Trade stats are net of commissions; drawdown is closed-trade based; Sharpe/
// Sortino use the daily marked-to-market account (backend backtest.js).

const fmt = (value, suffix = "", digits = 2) =>
  value == null || !Number.isFinite(+value)
    ? "--"
    : `${(+value).toLocaleString(undefined, {
        maximumFractionDigits: digits,
      })}${suffix}`;

const Tile = ({ label, tooltip, value, className = "" }) => (
  <div style={{ minWidth: 120 }}>
    <h5 style={{ opacity: 0.65 }}>
      {tooltip ? <DashedTooltip text={tooltip}>{label}</DashedTooltip> : label}
    </h5>
    <h3 className={className}>{value}</h3>
  </div>
);
Tile.propTypes = {
  label: PropTypes.string,
  tooltip: PropTypes.string,
  value: PropTypes.node,
  className: PropTypes.string,
};

const Group = ({ title, children }) => (
  <div className="text-left">
    <h4 className="mb-2" style={{ opacity: 0.8 }}>
      {title}
    </h4>
    <div className="flex flex-wrap gap-x-8 gap-y-3">{children}</div>
  </div>
);
Group.propTypes = { title: PropTypes.string, children: PropTypes.node };

const KeyMetrics = ({ backtestResults }) => {
  const m = backtestResults.metrics;
  const buyHold = m.buyHold || {};

  return (
    <GenericCard title="Key metrics" className="table-card" maxHeight={1000}>
      <div className="flex flex-col gap-6">
        <Group title="Return">
          <Tile
            label="CAGR"
            tooltip="Compound annual growth over the elapsed time of the backtest (closed trades)."
            value={fmt(m.cagr, "%")}
            className={returnUpDownClassname(m.cagr)}
          />
          <Tile
            label="Total return"
            tooltip="Closed-trade P&L over the whole range, net of commissions."
            value={fmt(m.pnlPercent, "%")}
            className={returnUpDownClassname(m.pnlPercent)}
          />
          <Tile
            label="Buy & hold CAGR"
            tooltip={`Holding the symbol over the same bars. Total ${fmt(
              m.buyHoldReturn,
              "%",
            )}.`}
            value={fmt(buyHold.cagr, "%")}
            className={returnUpDownClassname(buyHold.cagr)}
          />
          <Tile
            label="Positive months"
            tooltip="Months whose closed-trade equity grew, out of all months in the range (months without a closed trade count as flat)."
            value={`${fmt(m.monthlySuccessRate, "%", 1)} (${m.winningMonths}/${m.totalMonths})`}
          />
        </Group>

        <Group title="Risk">
          <Tile
            label="Max drawdown"
            tooltip="Largest drop of closed-trade equity from its peak (the peak starts at the initial balance)."
            value={fmt(m.maxDrawdown, "%")}
            className="red"
          />
          <Tile
            label="Calmar"
            tooltip="CAGR / |max drawdown|. Above 1 is decent, above 3 is strong."
            value={fmt(m.calmarRatio)}
          />
          <Tile
            label="Sharpe"
            tooltip="Annualized (mean - risk free) / std dev of daily account returns (open positions marked to the close, net of fees, 3% risk free). Above 1 is good, above 2 very good."
            value={fmt(m.sharpeRatio)}
          />
          <Tile
            label="Sortino"
            tooltip="Like Sharpe but only downside moves count as risk."
            value={fmt(m.sortinoRatio)}
          />
          <Tile
            label="Volatility"
            tooltip="Annualized std dev of daily account returns."
            value={fmt(m.volatility, "%")}
          />
          <Tile
            label="Time in market"
            tooltip="Share of bars with an open position."
            value={fmt(m.exposurePercent, "%", 1)}
          />
          <Tile
            label="Buy & hold max DD"
            tooltip="Largest peak-to-trough drop of the symbol's closes over the same bars."
            value={fmt(buyHold.maxDrawdown, "%")}
          />
        </Group>

        <Group title="Trades (net of commissions)">
          <Tile
            label="Trades"
            value={`${m.totalTransactions} (${fmt(m.avgTransactionsPerYear, "", 1)}/yr)`}
          />
          <Tile
            label="Win rate"
            tooltip="Trades with net P&L above 0."
            value={fmt(m.successRate, "%", 1)}
          />
          <Tile
            label="Profit factor"
            tooltip="Sum of winning trades / sum of losing trades. Above 1.5 is good."
            value={m.profitFactor == null ? "no losses" : fmt(m.profitFactor)}
          />
          <Tile
            label="Expectancy"
            tooltip="Average net % per trade."
            value={fmt(m.averageProfit, "%")}
            className={returnUpDownClassname(m.averageProfit)}
          />
          <Tile
            label="Avg win / avg loss"
            tooltip="Average net % of winning vs losing trades; payoff ratio in brackets."
            value={
              <>
                <span className="green">{fmt(m.avgProfit, "%")}</span> /{" "}
                <span className="red">{fmt(m.avgLoss, "%")}</span>{" "}
                <span style={{ opacity: 0.7 }}>({fmt(m.payoffRatio)})</span>
              </>
            }
          />
          <Tile
            label="Max losing streak"
            value={m.maxConsecutiveLosses}
          />
          <Tile
            label="Avg hold"
            tooltip="Bars held: all / winners / losers."
            value={`${fmt(m.averageDuration, "", 1)} / ${fmt(
              m.averageWinDuration,
              "",
              1,
            )} / ${fmt(m.averageLossDuration, "", 1)}`}
          />
          <Tile
            label="Hit target"
            tooltip="Trades that closed at or above the take profit %."
            value={fmt(m.strategySuccessRate, "%", 1)}
          />
          <Tile label="Commissions" value={`$${fmt(m.totalCommissions, "", 0)}`} />
        </Group>
      </div>
    </GenericCard>
  );
};

KeyMetrics.propTypes = { backtestResults: PropTypes.object };

export default KeyMetrics;
